import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { PenLine, Plus, Trash2, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

/**
 * Dialog to send a document for electronic signature.
 * Props:
 *   open: boolean
 *   document: { document_id, title } | null
 *   onClose: () => void
 *   onSent: () => void   // refresh parent
 */
const SendForSignatureDialog = ({ open, document, onClose, onSent }) => {
  const { token } = useAuth();
  const { locale } = useI18n();
  const isES = locale === "es";

  const [signers, setSigners] = useState([{ name: "", email: "", role: "" }]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [providerStatus, setProviderStatus] = useState(null); // {configured, provider}
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSigners([{ name: "", email: "", role: "" }]);
    setSubject(document ? `${isES ? "Firma requerida" : "Signature required"}: ${document.title}` : "");
    setMessage(isES
      ? "Por favor revisa y firma este documento."
      : "Please review and sign this document.");
    setLastResult(null);
    // Fetch provider status
    axios.get(`${API}/esignature/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProviderStatus(r.data))
      .catch(() => setProviderStatus(null));
  }, [open, document, token, isES]);

  const addSigner = () => setSigners([...signers, { name: "", email: "", role: "" }]);
  const removeSigner = (idx) => setSigners(signers.filter((_, i) => i !== idx));
  const updateSigner = (idx, patch) => {
    setSigners(signers.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const handleSend = async () => {
    if (!document) return;
    for (const s of signers) {
      if (!s.name || !s.email) {
        toast.error(isES ? "Nombre y email son obligatorios para cada firmante" : "Each signer requires name + email");
        return;
      }
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/documents/${document.document_id}/send-for-signature`,
        {
          signers: signers.map((s, i) => ({
            name: s.name,
            email: s.email,
            role: s.role || undefined,
            order: i + 1,
          })),
          subject,
          message,
          ordered: false,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastResult(res.data);
      const msg = res.data.mock
        ? (isES ? "Enviado en modo MOCK (sin proveedor configurado)" : "Sent in MOCK mode (no provider configured)")
        : (isES ? `Enviado vía ${res.data.provider}` : `Sent via ${res.data.provider}`);
      toast.success(msg);
      if (onSent) onSent();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al enviar a firma" : "Send for signature failed"));
    } finally {
      setSending(false);
    }
  };

  const isMockMode = !providerStatus?.configured;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="send-for-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="w-5 h-5 text-primary" />
            {isES ? "Enviar a firma electrónica" : "Send for electronic signature"}
          </DialogTitle>
          <DialogDescription>
            {document && <><span className="font-medium">{document.title}</span></>}
          </DialogDescription>
        </DialogHeader>

        {/* Provider status banner */}
        <div className={`flex items-start gap-2 p-3 rounded-md border ${
          isMockMode ? "bg-amber-500/5 border-amber-500/40" : "bg-primary/5 border-primary/40"
        }`}>
          <AlertCircle className={`w-4 h-4 mt-0.5 ${isMockMode ? "text-amber-500" : "text-primary"}`} />
          <div className="text-xs flex-1">
            {isMockMode ? (
              <>
                <p className="font-medium">
                  {isES ? "Modo MOCK (sin proveedor configurado)" : "MOCK mode (no provider configured)"}
                </p>
                <p className="text-muted-foreground">
                  {isES
                    ? "La solicitud se registrará pero NO se enviará a un proveedor real. "
                    : "The request will be recorded but NOT sent to a real provider. "}
                  <a href="/esignature-settings" className="text-primary hover:underline inline-flex items-center gap-1">
                    {isES ? "Configurar proveedor" : "Configure provider"} <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </>
            ) : (
              <p>
                {isES ? "Proveedor activo" : "Active provider"}: <Badge variant="outline">{providerStatus?.provider}</Badge>
              </p>
            )}
          </div>
        </div>

        {/* Signers */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{isES ? "Firmantes" : "Signers"}</Label>
            <Button size="sm" variant="outline" onClick={addSigner} data-testid="add-signer-btn">
              <Plus className="w-3 h-3 mr-1" />
              {isES ? "Añadir firmante" : "Add signer"}
            </Button>
          </div>
          {signers.map((s, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <Label htmlFor={`signer-${idx}-name`} className="text-xs">{isES ? "Nombre" : "Name"}</Label>
                <Input
                  id={`signer-${idx}-name`}
                  value={s.name}
                  onChange={(e) => updateSigner(idx, { name: e.target.value })}
                  data-testid={`signer-name-${idx}`}
                />
              </div>
              <div className="col-span-5">
                <Label htmlFor={`signer-${idx}-email`} className="text-xs">Email</Label>
                <Input
                  id={`signer-${idx}-email`}
                  type="email"
                  value={s.email}
                  onChange={(e) => updateSigner(idx, { email: e.target.value })}
                  data-testid={`signer-email-${idx}`}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor={`signer-${idx}-role`} className="text-xs">{isES ? "Rol" : "Role"}</Label>
                <Input
                  id={`signer-${idx}-role`}
                  value={s.role}
                  onChange={(e) => updateSigner(idx, { role: e.target.value })}
                  placeholder={isES ? "Aprobador" : "Approver"}
                />
              </div>
              <div className="col-span-1">
                {signers.length > 1 && (
                  <Button size="icon" variant="ghost" onClick={() => removeSigner(idx)}
                    data-testid={`remove-signer-${idx}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label htmlFor="esign-subject">{isES ? "Asunto" : "Subject"}</Label>
          <Input id="esign-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            data-testid="esign-subject-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="esign-message">{isES ? "Mensaje" : "Message"}</Label>
          <Textarea id="esign-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
            data-testid="esign-message-input" />
        </div>

        {lastResult && (
          <div className="p-3 bg-muted/50 rounded-md text-xs space-y-1">
            <div><strong>{isES ? "ID de solicitud" : "Request ID"}:</strong> {lastResult.signature_request_id}</div>
            <div className="flex items-center gap-1">
              <strong>{isES ? "Proveedor" : "Provider"}:</strong>
              <span>{lastResult.provider}</span>
              {lastResult.mock && <Badge variant="outline" className="ml-1">MOCK</Badge>}
            </div>
            <div><strong>{isES ? "Estado" : "Status"}:</strong> {lastResult.status}</div>
            {lastResult.mock && lastResult.provider === "docusign" && (
              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                {isES
                  ? "⚠ DocuSign aún no está implementado para envío real. La solicitud se registró en modo MOCK."
                  : "⚠ DocuSign live send is not implemented yet. The request was recorded in MOCK mode."}
              </div>
            )}
            {lastResult.details_url && (
              <a href={lastResult.details_url} target="_blank" rel="noopener noreferrer"
                 className="text-primary hover:underline inline-flex items-center gap-1">
                {isES ? "Ver en proveedor" : "View at provider"} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{isES ? "Cerrar" : "Close"}</Button>
          <Button onClick={handleSend} disabled={sending || !document} data-testid="esign-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
            {isES ? "Enviar a firmar" : "Send for signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendForSignatureDialog;
